import { useQuery } from '@tanstack/react-query';
import { paymentListAdapter } from '../libs/paymentListAdapter';
import { checkoutAPI } from '../api/checkoutAPI';
import { FulfillmentMode } from '../../../store/slices/fulfillment';
import { IPayemntsList } from '../types/PayemntsList';

const EMPTY: IPayemntsList[] = [];

/**
 * Способы оплаты филиала — `branch/payment-list`. Единственный источник того,
 * что показывать в блоке «Оплата»: своих списков и условий на фронте нет.
 *
 * Состояния отдаём раздельно, а не пустым массивом по умолчанию: «ещё грузится»,
 * «ошибка» и «бэкенд вернул пусто» на экране ведут себя по-разному, и ни в одном
 * из них нельзя оформить заказ ранее выбранным способом.
 */
export const useGetPaymentList = (
  branch?: string,
  orderType: FulfillmentMode = 'delivery'
) => {
  const query = useQuery({
    // Филиал и тип заказа меняют состав способов — оба в ключе. Новый ключ
    // начинается без данных: список прошлого филиала не подставляется.
    queryKey: ['payment-list', branch, orderType],
    queryFn: () => checkoutAPI.getPayment(branch!, orderType),
    select: data => paymentListAdapter(data.data),
    enabled: !!branch,
  });

  const paymentList = query.data ?? EMPTY;

  // Состав приходит с бэкенда — печатаем его на стенде, чтобы не гадать, почему
  // способа нет на экране. В прод-сборку не попадает.
  if (import.meta.env.DEV && query.isSuccess) {
    console.info('[payment-list]', paymentList);
  }

  return {
    paymentList,
    // Без филиала списка нет и быть не может — это тоже «не готово».
    isLoading: !branch || query.isPending,
    isError: query.isError,
    /** Актуальный список получен: только с ним можно оформлять заказ. */
    isReady: query.isSuccess,
    refetch: query.refetch,
  };
};
